/**
 * Player controller: movimentação 8 direções relativa à câmera (sem ganho na diagonal),
 * aceleração/desaceleração suaves, rotação para o movimento/ataque, dash e ataque
 * com janela real de dano (windup → hitframe → recovery). Implementa Damageable.
 */
import * as THREE from "three";
import { GameConfig } from "../config/GameConfig";
import type { GameInput } from "../core/GameInput";
import type { Arena } from "../world/Arena";
import type { Sanctuary, SanctuaryZone } from "../world/Sanctuary";
import type { CombatStats } from "../combat/CombatStats";
import type { Damageable } from "../combat/Damageable";
import type { ProjectileManager } from "../combat/ProjectileManager";
import type { DamageType, EffectiveWeapon, StatMods, EquipmentInstance, EquipmentSlot } from "../items/EquipmentTypes";
import { HunterEquipment } from "./HunterEquipment";
import { HealthComponent } from "../combat/HealthComponent";
import { dealDamage } from "../combat/DamageSystem";
import { events } from "../core/Events";
import { applyRim } from "../fx/RimLight";
import { skinTexture } from "../enemies/CreatureSkins";
import { clamp, dampAngle } from "../utils/math";
import { addContactShadow } from "../enemies/CreatureBuilder";
import { createPlayerVisual, weaponSocketOffset, attachHunterIdentity, type RiggedVisual, type AnimState } from "../models";
import { wallBetween } from "../world/TerritoryGates";

function unarmedWeapon(): EffectiveWeapon {
  const u = GameConfig.weapon.unarmed;
  return {
    weaponType: u.weaponType,
    attackType: u.attackType,
    damageType: u.damageType,
    attackRange: u.attackRange,
    attacksPerSecond: u.attacksPerSecond,
    arc: u.arc,
    knockback: u.knockback,
    projectileSpeed: u.projectileSpeed,
    visualId: u.visualId,
  };
}

export type PlayerState = "idle" | "walking" | "attacking" | "sprinting" | "hit" | "dead";

// temporários reutilizados (zero alocação por frame)
const _camF = new THREE.Vector3();
const _camR = new THREE.Vector3();
const _move = new THREE.Vector3();
const _aim = new THREE.Vector3();
const _to = new THREE.Vector3();
const _hits: { e: Damageable; d: number }[] = [];

function approach(cur: number, target: number, maxDelta: number): number {
  const d = target - cur;
  if (Math.abs(d) <= maxDelta) return target;
  return cur + Math.sign(d) * maxDelta;
}

/** Alvo é Elite ou Boss? (Enemy.elite / VoidAlpha.phaseNumber) — p/ dano condicional. */
function isEliteOrBoss(d: Damageable): boolean {
  const x = d as { elite?: boolean; phaseNumber?: number };
  return x.elite === true || typeof x.phaseNumber === "number";
}

/** Arma VISÍVEL estilizada por visualId (segura na mão; aponta para +Y no anchor).
 * Exportada: a CORRENTE (ChainWeapons) reusa a mesma arte para as armas orbitais. */
export interface WeaponLook {
  /** 10.6E — cor de ENERGIA da arma. Vem da RARIDADE: um lendário brilha dourado, um
   *  mítico vermelho. Antes toda arma do jogo tinha o mesmo veio ciano, então um item
   *  raro e um comum eram visualmente idênticos na mão. */
  energy?: number;
  /** Multiplicador de brilho: quanto mais rara, mais o veio queima. */
  glow?: number;
  /** 10.9GX — TIER de poder (1..14): muda a liga do metal, o cabo e acrescenta adornos. */
  tier?: number;
  /** 10.9GZ — FAMÍLIA (prefixo do id: eclipse, umbral, astral, sideral, stellar, nebular, abyssal, omega): silhueta própria. */
  family?: string;
}

/** 10.9GZ — SILHUETA POR FAMÍLIA: cada linhagem de arma de boss tem uma forma reconhecível de longe. */
const WEAPON_FAMILY: Record<string, (g: THREE.Group, energy: (c: number) => THREE.MeshStandardMaterial, dark: THREE.MeshStandardMaterial) => void> = {
  eclipse: (g, energy, dark) => {
    // disco negro do eclipse atrás da cabeça da arma, com a coroa âmbar aparecendo por trás
    const disc = new THREE.Mesh(new THREE.CircleGeometry(0.24, 20), dark); disc.position.set(0, 1.0, -0.06); g.add(disc);
    const corona = new THREE.Mesh(new THREE.TorusGeometry(0.25, 0.028, 6, 24), energy(0xff9a3c)); corona.position.set(0, 1.0, -0.07); g.add(corona);
    const cres = new THREE.Mesh(new THREE.TorusGeometry(0.17, 0.02, 6, 16, Math.PI), energy(0xff9a3c)); cres.position.set(0, 0.42, 0); cres.rotation.x = Math.PI / 2; g.add(cres);
  },
  umbral: (g, energy, dark) => {
    // tentáculos de sombra saindo da guarda + gema roxa
    for (let i = 0; i < 3; i++) { const a = (i / 3) * Math.PI * 2; const t = new THREE.Mesh(new THREE.ConeGeometry(0.03, 0.55, 4), dark); t.position.set(Math.cos(a) * 0.12, 0.62, Math.sin(a) * 0.12); t.rotation.set(Math.sin(a) * 0.55, 0, -Math.cos(a) * 0.55); g.add(t); }
    const gem = new THREE.Mesh(new THREE.OctahedronGeometry(0.06, 0), energy(0x8e4dff)); gem.position.y = 0.44; g.add(gem);
  },
  astral: (g, energy) => {
    // pontas de estrela girando em volta da cabeça
    const m = energy(0xd9a6ff);
    for (let i = 0; i < 5; i++) { const a = (i / 5) * Math.PI * 2; const s = new THREE.Mesh(new THREE.TetrahedronGeometry(0.045, 0), m); s.position.set(Math.cos(a) * 0.2, 0.95 + Math.sin(a * 2) * 0.12, Math.sin(a) * 0.2); s.rotation.set(a, a * 0.7, 0); g.add(s); }
  },
  sideral: (g, energy) => {
    // duas lascas de gelo ladeando a lâmina
    const m = energy(0x9fe6ff);
    for (const s of [-1, 1]) { const sh = new THREE.Mesh(new THREE.OctahedronGeometry(0.05, 0), m); sh.position.set(s * 0.15, 0.85, 0); sh.scale.set(0.6, 4.2, 0.6); sh.rotation.z = s * 0.12; g.add(sh); }
  },
  stellar: (g, energy) => {
    // dois anéis de órbita cruzados
    const m = energy(0x7fd6ff);
    for (const r of [0.45, -0.45]) { const ring = new THREE.Mesh(new THREE.TorusGeometry(0.22, 0.014, 6, 24), m); ring.position.y = 1.0; ring.rotation.set(Math.PI / 2 + r, 0, r); g.add(ring); }
  },
  nebular: (g, energy) => {
    // nó de nebulosa carmesim na cabeça da arma
    const k = new THREE.Mesh(new THREE.TorusKnotGeometry(0.11, 0.025, 48, 6, 2, 3), energy(0xff4d6d)); k.position.y = 1.08; g.add(k);
  },
  abyssal: (g, energy, dark) => {
    // corrente enrolada no cabo + gancho abissal
    for (let i = 0; i < 5; i++) { const l = new THREE.Mesh(new THREE.TorusGeometry(0.045, 0.012, 5, 10), dark); l.position.set(i % 2 ? 0.03 : -0.03, 0.42 + i * 0.1, 0); l.rotation.set(i % 2 ? Math.PI / 2 : 0, i % 2 ? 0 : Math.PI / 2, 0); g.add(l); }
    const hook = new THREE.Mesh(new THREE.TorusGeometry(0.11, 0.025, 6, 14, Math.PI * 1.3), energy(0xff3d5d)); hook.position.set(0.1, 1.22, 0); hook.rotation.z = -0.4; g.add(hook);
  },
  omega: (g, energy) => {
    // asas douradas na guarda + halo
    const m = energy(0xfff2c8);
    for (const s of [-1, 1]) { const w = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.06, 0.02), m); w.position.set(s * 0.22, 0.46, 0); w.rotation.z = s * 0.55; g.add(w); const w2 = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.045, 0.02), m); w2.position.set(s * 0.2, 0.36, 0); w2.rotation.z = s * 0.25; g.add(w2); }
    const halo = new THREE.Mesh(new THREE.TorusGeometry(0.26, 0.018, 6, 24), m); halo.position.y = 1.02; halo.rotation.x = Math.PI / 2 + 0.35; g.add(halo);
  },
};

/**
 * 10.9GX — SKIN POR TIER. Um sabre T14 e um T3 usavam o MESMO mesh (só a cor da raridade mudava):
 * a arma mais forte do jogo era visualmente igual à de Nv 30. Agora cada faixa de tier tem liga de
 * metal, cabo e adornos próprios (crescente do Eclipse, lascas orbitando, anel sideral, espinhos
 * abissais, halo do Ômega) e cresce um pouco. Vale pra mão, pra corrente e pras arenas.
 */
const WEAPON_TIER: { min: number; steel: number; dark: number; wrap: number; accent: number; emis: number }[] = [
  { min: 4, steel: 0xb9c2f2, dark: 0x3b3566, wrap: 0x2a2450, accent: 0x9d7bff, emis: 1.0 },
  { min: 7, steel: 0x3a2f45, dark: 0x1d1626, wrap: 0x2b1f1f, accent: 0xff9a3c, emis: 1.15 }, // Eclipse
  { min: 8, steel: 0x6a4fa8, dark: 0x2a1c4c, wrap: 0x1e1638, accent: 0xc86bff, emis: 1.2 }, // Umbral / Astral
  { min: 10, steel: 0x9fc8ff, dark: 0x1b2a4a, wrap: 0x14203a, accent: 0x62e8ff, emis: 1.25 }, // Sideral / Estelar
  { min: 12, steel: 0x7a2a3a, dark: 0x2c1030, wrap: 0x1e0c22, accent: 0xff4d6d, emis: 1.3 }, // Nebular / Abissal
  { min: 14, steel: 0xfff2d0, dark: 0x3a3a48, wrap: 0x2c2a3c, accent: 0xffffff, emis: 1.4 }, // Ômega
];
function applyWeaponTierSkin(g: THREE.Group, tier: number, mats: { steel: THREE.MeshStandardMaterial; darkMetal: THREE.MeshStandardMaterial; wrap: THREE.MeshStandardMaterial }, energy: (c: number) => THREE.MeshStandardMaterial, family?: string): void {
  let p = WEAPON_TIER[0];
  for (const t of WEAPON_TIER) if (tier >= t.min) p = t;
  mats.steel.color.setHex(p.steel);
  mats.darkMetal.color.setHex(p.dark);
  mats.wrap.color.setHex(p.wrap);
  if (tier >= 7) { mats.steel.metalness = 0.85; mats.steel.roughness = 0.22; }
  // veios queimam um pouco mais forte (teto 1.4: o bloom tem threshold 0.85)
  g.traverse((o) => {
    const m = (o as THREE.Mesh).material as THREE.MeshStandardMaterial | undefined;
    if (m && m.emissiveIntensity >= 0.85) m.emissiveIntensity = Math.min(1.4, m.emissiveIntensity * p.emis);
  });
  const fam = family && WEAPON_FAMILY[family];
  if (fam) { fam(g, energy, mats.darkMetal); return; } // 10.9GZ — família tem silhueta própria (substitui o adorno genérico)
  const acc = energy(p.accent);
  const add = (geo: THREE.BufferGeometry, x: number, y: number, z: number, rx = 0, ry = 0, rz = 0): THREE.Mesh => {
    const m = new THREE.Mesh(geo, acc); m.position.set(x, y, z); m.rotation.set(rx, ry, rz); g.add(m); return m;
  };
  if (tier >= 14) {
    // ÔMEGA: halo inclinado no meio da arma + 3 gemas brancas
    add(new THREE.TorusGeometry(0.26, 0.018, 6, 24), 0, 1.0, 0, Math.PI / 2 + 0.35, 0, 0);
    for (let i = 0; i < 3; i++) { const a = (i / 3) * Math.PI * 2; add(new THREE.OctahedronGeometry(0.045, 0), Math.cos(a) * 0.2, 0.55 + i * 0.12, Math.sin(a) * 0.2); }
  } else if (tier >= 12) {
    // NEBULAR/ABISSAL: espinhos alternados ao longo da lâmina
    for (let i = 0; i < 4; i++) { const s = i % 2 ? 1 : -1; add(new THREE.ConeGeometry(0.03, 0.16, 4), s * 0.09, 0.62 + i * 0.18, 0, 0, 0, s * -Math.PI / 2); }
  } else if (tier >= 10) {
    // SIDERAL/ESTELAR: anel horizontal no meio + gema
    add(new THREE.TorusGeometry(0.19, 0.016, 6, 20), 0, 0.72, 0, Math.PI / 2, 0, 0);
    add(new THREE.OctahedronGeometry(0.05, 0), 0, 0.72, 0.19);
  } else if (tier >= 8) {
    // UMBRAL/ASTRAL: 3 lascas de cristal orbitando a arma
    for (let i = 0; i < 3; i++) { const a = (i / 3) * Math.PI * 2; const sh = add(new THREE.OctahedronGeometry(0.045, 0), Math.cos(a) * 0.19, 0.55 + i * 0.22, Math.sin(a) * 0.19); sh.scale.set(0.7, 1.9, 0.7); }
  } else if (tier >= 7) {
    // ECLIPSE: crescente âmbar sobre a guarda
    add(new THREE.TorusGeometry(0.17, 0.02, 6, 16, Math.PI), 0, 0.42, 0, Math.PI / 2, 0, 0);
  } else {
    // T4–6: duas gemas flutuando junto da guarda
    for (const s of [-1, 1]) add(new THREE.OctahedronGeometry(0.04, 0), s * 0.17, 0.46, 0);
  }
}

export function buildWeaponMesh(visualId: string, look?: WeaponLook): THREE.Object3D {
  const g = new THREE.Group();
  const eHex = look?.energy;
  const eGlow = look?.glow ?? 1;
  const steel = new THREE.MeshStandardMaterial({ color: 0xcfd6e6, roughness: 0.32, metalness: 0.78, flatShading: true });
  const darkMetal = new THREE.MeshStandardMaterial({ color: 0x39354e, roughness: 0.5, metalness: 0.6, flatShading: true });
  // itens de raridade alta puxam o metal escuro para a cor da energia (liga "encantada")
  if (eHex !== undefined && eGlow > 1.25) darkMetal.color.lerp(new THREE.Color(eHex), 0.3);
  const wrap = new THREE.MeshStandardMaterial({ color: 0x211d33, roughness: 0.95, flatShading: true });
  const energy = (c: number): THREE.MeshStandardMaterial => {
    const col = eHex ?? c;
    // 10.9L — brilho MUITO mais contido: antes 2.4×glow (comum 2.4 → mítica 4.8) estourava com o
    // bloom e "lavava" a arma (não dava pra ver o formato nem as melhorias). Agora 0.9→1.4.
    return new THREE.MeshStandardMaterial({ color: col, emissive: col, emissiveIntensity: 0.9 + (eGlow - 1) * 0.5, roughness: 0.2, flatShading: true });
  };
  const stone = new THREE.MeshStandardMaterial({ color: 0x6b7280, roughness: 1, flatShading: true });

  // cabo: grip enrolado com ANÉIS + guarda curvada + pomo + gemas nas pontas da guarda (10.5K)
  const hilt = (guardW: number, gem: number): void => {
    const grip = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.052, 0.3, 8), wrap); grip.position.y = 0.18; g.add(grip);
    for (const y of [0.09, 0.17, 0.25]) { const ring = new THREE.Mesh(new THREE.TorusGeometry(0.05, 0.012, 5, 10), darkMetal); ring.rotation.x = Math.PI / 2; ring.position.y = y; g.add(ring); }
    for (const s of [-1, 1]) {
      const arm = new THREE.Mesh(new THREE.BoxGeometry(guardW * 0.5, 0.06, 0.1), steel);
      arm.position.set(s * guardW * 0.25, 0.345, 0); arm.rotation.z = s * 0.18; g.add(arm);
      const t = new THREE.Mesh(new THREE.OctahedronGeometry(0.035, 0), energy(gem)); t.position.set(s * guardW * 0.5, 0.38, 0); g.add(t);
    }
    const boss = new THREE.Mesh(new THREE.OctahedronGeometry(0.05, 0), darkMetal); boss.position.y = 0.35; boss.scale.set(1, 0.7, 1.3); g.add(boss);
    const pommel = new THREE.Mesh(new THREE.OctahedronGeometry(0.055, 0), energy(gem)); pommel.position.y = 0.02; g.add(pommel);
  };

  // lâmina de aço com FIO emissivo dos dois lados + fuller + RUNAS + ponta; monta o cabo padrão
  const blade = (len: number, edgeColor = 0x8fe8ff): void => {
    const b = new THREE.Mesh(new THREE.BoxGeometry(0.1, len, 0.035), steel); b.position.y = 0.35 + len / 2; b.castShadow = true; g.add(b);
    const tip = new THREE.Mesh(new THREE.ConeGeometry(0.052, 0.18, 4), steel); tip.position.y = 0.35 + len + 0.07; tip.rotation.y = Math.PI / 4; g.add(tip);
    const ed = new THREE.Mesh(new THREE.BoxGeometry(0.022, len, 0.042), energy(edgeColor)); ed.position.set(0.052, 0.35 + len / 2, 0); g.add(ed);
    const ed2 = new THREE.Mesh(new THREE.BoxGeometry(0.014, len * 0.9, 0.04), energy(edgeColor)); ed2.position.set(-0.05, 0.35 + len / 2, 0); g.add(ed2);
    const fuller = new THREE.Mesh(new THREE.BoxGeometry(0.02, len * 0.82, 0.05), darkMetal); fuller.position.y = 0.35 + len / 2; g.add(fuller);
    for (let i = 0; i < 4; i++) { const rune = new THREE.Mesh(new THREE.BoxGeometry(0.03, 0.05, 0.052), energy(edgeColor)); rune.position.y = 0.5 + i * (len * 0.16); g.add(rune); }
    hilt(0.34, edgeColor);
  };

  // pega de uma arma de haste: anéis de metal + gema
  const haftRings = (ys: number[], r: number): void => {
    for (const y of ys) { const ring = new THREE.Mesh(new THREE.TorusGeometry(r, 0.014, 5, 10), steel); ring.rotation.x = Math.PI / 2; ring.position.y = y; g.add(ring); }
  };

  switch (visualId) {
    case "sword":
      blade(0.82, 0x8fe8ff);
      break;
    case "shardSaber": {
      const eb = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.92, 0.03), energy(0x62e8ff)); eb.position.y = 0.35 + 0.46; g.add(eb);
      const etip = new THREE.Mesh(new THREE.ConeGeometry(0.05, 0.18, 4), energy(0x62e8ff)); etip.position.y = 0.35 + 0.92 + 0.06; g.add(etip);
      hilt(0.3, 0x62e8ff);
      break;
    }
    case "axe": {
      // machado de DUAS lâminas (bipene) com veio de energia e cabo com anéis (10.5K)
      const haft = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.052, 1.1, 8), wrap); haft.position.y = 0.55; g.add(haft);
      haftRings([0.12, 0.3, 0.7], 0.052);
      for (const side of [1, -1]) {
        const head = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.4, 0.34), steel); head.position.set(side * 0.14, 0.98, 0); head.castShadow = true; g.add(head);
        for (const s of [-1, 1]) { const horn = new THREE.Mesh(new THREE.ConeGeometry(0.11, 0.26, 4), steel); horn.position.set(side * 0.2, 0.98 + s * 0.23, 0); horn.rotation.z = -side * Math.PI / 2; horn.rotation.x = s * 0.35; g.add(horn); }
        const edge = new THREE.Mesh(new THREE.BoxGeometry(0.02, 0.44, 0.36), energy(0x8fe8ff)); edge.position.set(side * 0.19, 0.98, 0); g.add(edge);
        const vein = new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.03, 0.2), energy(0x8fe8ff)); vein.position.set(side * 0.13, 0.98, 0); g.add(vein);
      }
      const hub = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.07, 0.46, 6), darkMetal); hub.position.y = 0.98; g.add(hub);
      const ax = new THREE.Mesh(new THREE.OctahedronGeometry(0.08, 0), energy(0x8fe8ff)); ax.position.set(0, 0.98, 0.09); g.add(ax);
      const topSpike = new THREE.Mesh(new THREE.ConeGeometry(0.05, 0.24, 4), steel); topSpike.position.y = 1.32; g.add(topSpike);
      const pommel = new THREE.Mesh(new THREE.OctahedronGeometry(0.05, 0), energy(0x8fe8ff)); pommel.position.y = 0.02; g.add(pommel);
      break;
    }
    case "mace": {
      // clava FLANGEADA: cabo com anéis + núcleo + 6 aletas de aço + espinho no topo + gema laranja (10.5K)
      const haft = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.058, 1.0, 8), wrap); haft.position.y = 0.5; g.add(haft);
      haftRings([0.1, 0.28, 0.62], 0.058);
      const core = new THREE.Mesh(new THREE.SphereGeometry(0.15, 8, 6), darkMetal); core.position.y = 1.02; core.castShadow = true; g.add(core);
      for (let i = 0; i < 6; i++) {
        const fl = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.36, 0.2), steel);
        const a = (i / 6) * Math.PI * 2;
        fl.position.set(Math.cos(a) * 0.17, 1.02, Math.sin(a) * 0.17);
        fl.rotation.y = -a;
        fl.castShadow = true;
        g.add(fl);
        const fe = new THREE.Mesh(new THREE.BoxGeometry(0.02, 0.3, 0.22), energy(0xff9a5a));
        fe.position.set(Math.cos(a) * 0.23, 1.02, Math.sin(a) * 0.23);
        fe.rotation.y = -a;
        g.add(fe);
      }
      const topSpike = new THREE.Mesh(new THREE.ConeGeometry(0.07, 0.24, 4), steel); topSpike.position.y = 1.32; g.add(topSpike);
      const gem = new THREE.Mesh(new THREE.OctahedronGeometry(0.1, 0), energy(0xff9a5a)); gem.position.y = 1.02; g.add(gem);
      const pommel = new THREE.Mesh(new THREE.OctahedronGeometry(0.05, 0), energy(0xff9a5a)); pommel.position.y = 0.02; g.add(pommel);
      break;
    }
    case "bow": {
      // arco RECURVO: braços em curva real (tubo), veios de energia, empunhadura enrolada e nocks (10.5K)
      const limb = new THREE.CatmullRomCurve3([
        new THREE.Vector3(0.22, 0.6 - 0.62, 0), new THREE.Vector3(0.36, 0.6 - 0.34, 0), new THREE.Vector3(0.3, 0.6 - 0.1, 0),
        new THREE.Vector3(0.3, 0.6 + 0.1, 0), new THREE.Vector3(0.36, 0.6 + 0.34, 0), new THREE.Vector3(0.22, 0.6 + 0.62, 0),
      ]);
      const limbMesh = new THREE.Mesh(new THREE.TubeGeometry(limb, 24, 0.035, 6, false), darkMetal); limbMesh.castShadow = true; g.add(limbMesh);
      const vein = new THREE.Mesh(new THREE.TubeGeometry(limb, 24, 0.014, 5, false), energy(0x8fe8ff)); vein.position.x = 0.03; g.add(vein);
      const riser = new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.26, 0.08), wrap); riser.position.set(0.3, 0.6, 0); g.add(riser);
      for (const y of [0.5, 0.6, 0.7]) { const ring = new THREE.Mesh(new THREE.TorusGeometry(0.05, 0.012, 5, 10), steel); ring.rotation.x = Math.PI / 2; ring.position.set(0.3, y, 0); g.add(ring); }
      const string = new THREE.Mesh(new THREE.CylinderGeometry(0.007, 0.007, 1.24, 4), steel); string.position.set(0.22, 0.6, 0); g.add(string);
      for (const s of [-1, 1]) { const nock = new THREE.Mesh(new THREE.OctahedronGeometry(0.05, 0), energy(0x8fe8ff)); nock.position.set(0.22, 0.6 + s * 0.62, 0); g.add(nock); }
      const sight = new THREE.Mesh(new THREE.OctahedronGeometry(0.06, 0), energy(0x8fe8ff)); sight.position.set(0.3, 0.86, 0); sight.scale.set(0.6, 1, 0.6); g.add(sight);
      break;
    }
    // ---- Fase 10: armas Tier 3 (Tempest Reach) — silhuetas próprias (§29) ----
    case "galeBlade": {
      // lâmina de energia esguia + fins varridos (vento)
      blade(0.95, 0x9fe6ff);
      for (const s of [-1, 1]) {
        const fin = new THREE.Mesh(new THREE.ConeGeometry(0.08, 0.3, 4), energy(0x7fd6ff));
        fin.position.set(s * 0.13, 0.55, 0);
        fin.rotation.z = s * 0.7;
        g.add(fin);
      }
      break;
    }
    case "tempestCleaver": {
      const haft = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.06, 1.05, 6), stone);
      haft.position.y = 0.5;
      g.add(haft);
      const head = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.5, 0.52), energy(0x8fd8ff));
      head.position.set(0.16, 0.95, 0);
      head.rotation.z = 0.2;
      g.add(head);
      const spike = new THREE.Mesh(new THREE.ConeGeometry(0.1, 0.32, 4), stone);
      spike.position.y = 1.28;
      g.add(spike);
      break;
    }
    case "arcLongbow": {
      const arc = new THREE.Mesh(new THREE.TorusGeometry(0.62, 0.05, 6, 16, Math.PI * 1.35), energy(0x9fe6ff));
      arc.position.y = 0.6;
      arc.rotation.z = Math.PI / 2;
      g.add(arc);
      const string = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.012, 1.05, 4), steel);
      string.position.y = 0.6;
      g.add(string);
      const grip = new THREE.Mesh(new THREE.OctahedronGeometry(0.09, 0), energy(0xbfefff));
      grip.position.y = 0.6;
      g.add(grip);
      break;
    }
    case "staff": {
      // cajado arcano: haste longa + garras metálicas segurando um ORBE de energia flutuante
      const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.058, 1.24, 7), darkMetal);
      shaft.position.y = 0.5; shaft.castShadow = true; g.add(shaft);
      haftRings([0.18, 0.42, 0.66], 0.062);
      const grip = new THREE.Mesh(new THREE.CylinderGeometry(0.055, 0.055, 0.26, 8), wrap); grip.position.y = 0.3; g.add(grip);
      // coroa de 3 garras no topo, abrindo pra segurar o orbe
      for (const ang of [0, (Math.PI * 2) / 3, (Math.PI * 4) / 3]) {
        const claw = new THREE.Mesh(new THREE.ConeGeometry(0.045, 0.34, 4), steel);
        claw.position.set(Math.sin(ang) * 0.12, 1.16, Math.cos(ang) * 0.12);
        claw.rotation.set(Math.cos(ang) * 0.5, 0, -Math.sin(ang) * 0.5);
        g.add(claw);
      }
      const orb = new THREE.Mesh(new THREE.IcosahedronGeometry(0.15, 0), energy(0xb48bff)); orb.position.y = 1.28; g.add(orb);
      const halo = new THREE.Mesh(new THREE.TorusGeometry(0.21, 0.02, 6, 16), energy(0xb48bff)); halo.position.y = 1.28; halo.rotation.x = Math.PI / 2; g.add(halo);
      break;
    }
    case "tome": {
      // 10.9L25 — grimório: livro flutuante (capa + lombada) com página aberta e runa orbitando
      const cover = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.62, 0.08), darkMetal); cover.position.set(0, 1.0, 0); cover.castShadow = true; g.add(cover);
      const spine = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.64, 0.1), steel); spine.position.set(0, 1.0, 0); g.add(spine);
      const pages = new THREE.Mesh(new THREE.BoxGeometry(0.42, 0.5, 0.05), wrap); pages.position.set(0.02, 1.0, 0.05); g.add(pages);
      const rune = new THREE.Mesh(new THREE.OctahedronGeometry(0.09, 0), energy(0xb48bff)); rune.position.set(0.02, 1.0, 0.16); g.add(rune);
      const halo = new THREE.Mesh(new THREE.TorusGeometry(0.16, 0.015, 6, 16), energy(0xb48bff)); halo.position.set(0.02, 1.0, 0.16); g.add(halo);
      // cabo curto embaixo (pra segurar como um livro-cetro)
      const grip = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.05, 0.66, 7), wrap); grip.position.y = 0.33; g.add(grip);
      haftRings([0.14, 0.3], 0.052);
      break;
    }
    case "crossbow": {
      // 10.9L25 — besta compacta: trilho + arco curto travado nas duas pontas + gatilho
      const rail = new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.06, 0.08), darkMetal); rail.rotation.z = Math.PI / 2; rail.position.set(0.05, 0.62, 0); rail.castShadow = true; g.add(rail);
      const limb = new THREE.CatmullRomCurve3([
        new THREE.Vector3(0.05, 0.62 - 0.34, 0), new THREE.Vector3(0.24, 0.62 - 0.18, 0), new THREE.Vector3(0.24, 0.62 + 0.18, 0), new THREE.Vector3(0.05, 0.62 + 0.34, 0),
      ]);
      const limbMesh = new THREE.Mesh(new THREE.TubeGeometry(limb, 16, 0.03, 6, false), steel); g.add(limbMesh);
      const string = new THREE.Mesh(new THREE.CylinderGeometry(0.007, 0.007, 0.68, 4), steel); string.rotation.x = Math.PI / 2; string.position.set(0.22, 0.62, 0); g.add(string);
      const boltRest = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.03, 0.03), energy(0x8fe8ff)); boltRest.position.set(0.05, 0.66, 0); g.add(boltRest);
      const grip = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.055, 0.4, 7), wrap); grip.position.set(-0.28, 0.4, 0); grip.rotation.z = 0.35; g.add(grip);
      const trigger = new THREE.Mesh(new THREE.OctahedronGeometry(0.045, 0), energy(0x8fe8ff)); trigger.position.set(-0.14, 0.5, 0); g.add(trigger);
      break;
    }
    default: // fist / unarmed: manopla discreta
      g.add(new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.16, 0.16), steel));
      break;
  }
  // 10.9GX — skin por tier (T4+): liga, cabo e adornos
  const tier = look?.tier ?? 1;
  if (visualId !== "fist" && tier >= 4) applyWeaponTierSkin(g, tier, { steel, darkMetal, wrap }, energy, look?.family);
  // presença: arma maior p/ leitura clara na câmera de cima (não é gameplay).
  // 10.6E: 1.28 -> 1.46. Com a câmera aberta (27 m) a arma virava um risco fino na tela.
  if (visualId !== "fist") g.scale.setScalar(1.46 * (1 + Math.min(0.12, Math.max(0, tier - 4) * 0.012))); // 10.9GX: tier alto cresce até +12%
  return g;
}

export class Player implements Damageable {
  readonly group = new THREE.Group();
  readonly health: HealthComponent;
  readonly combatStats: CombatStats;

  state: PlayerState = "idle";
  facingAngle = 0;

  private readonly cfg = GameConfig.player;
  private effWeapon: EffectiveWeapon = unarmedWeapon(); // arma equipada (ou desarmado)
  private traits: ReadonlySet<string> = new Set();
  private projectiles: ProjectileManager | null = null;
  private meleeHitCount = 0; // p/ Void Edge (sword)
  private bowShotCount = 0; // p/ Void Shot (bow)
  private moveSpeed: number = GameConfig.player.moveSpeed; // efetivo (recalculado)
  private readonly velocity = new THREE.Vector3();
  /** 10.9AI — velocidade atual (leitura): os atiradores inimigos antecipam a mira por ela. */
  get moveVelocity(): THREE.Vector3 { return this.velocity; }
  private readonly knockback = new THREE.Vector3();

  // sprint (substitui o dash): aumento temporário e controlável de velocidade
  private sprintTimer = 0;
  private sprintCooldown = 0;
  private _invuln = false;
  private spawnProtect = 0; // proteção pós-respawn
  private sinceHit = 999; // s desde o último dano (gate da regen da base)

  // ---- Void Tree (mods efetivos + estado dos nós especiais) ----
  private effHookRangeMult = 0;
  private effHookCdReduc = 0;
  private effSprintDuration: number = GameConfig.player.sprint.duration;
  private effSprintCooldown: number = GameConfig.player.sprint.cooldown;
  private vsEliteMult = 0;
  private executeTreeMult = 0;
  private resilience = 0;
  // 10.6U — regen FLAT permanente comprada com 💎 (soma independente do regenPct)
  private flatRegenPerSec = 0;
  setFlatRegen(v: number): void { this.flatRegenPerSec = Math.max(0, v); }
  get flatRegen(): number { return this.flatRegenPerSec; }

  // 10.5K — defesa por tipo / bloqueio / regeneração contínua (Void Tree cobre todos os stats)
  private typeDef: Partial<Record<DamageType, number>> = {};
  private _blockChance = 0;
  private regenPct = 0;
  private unbreakableCd = 0; // >0 = em recarga
  private secondWindCd = 0;
  private voidStepTimer = 0; // janela de redução no início do sprint
  private predatorTimer = 0; // buff de move após kill
  private domKills: number[] = []; // timestamps (s acumulados) de kills p/ Void Dominance
  private domBuffTimer = 0;
  private clock = 0;

  // auto-attack
  private attackPhaseTimer = 0;
  private attackCooldown = 0;
  private attackPhase: "none" | "windup" | "hit" | "recovery" = "none";
  private attackHitDone = false;
  private aimAngle = 0;
  private currentTarget: Damageable | null = null;

  // ---- Combat zone (Void Sanctuary) — histerese anti-cheese ----
  private zone: SanctuaryZone = "combat";
  private _inSanctuary = false;
  private _combatEnabled = false; // liga só ao sair TOTALMENTE do buffer; desliga ao entrar nele

  private hitStun = 0;
  private flashTimer = 0;

  private readonly bodyMat: THREE.MeshStandardMaterial;
  // 10.4Z — materiais de roupa (recoloríveis pelo guarda-roupa); preenchidos em buildHunter
  private matDark?: THREE.MeshStandardMaterial;
  private matAccent?: THREE.MeshStandardMaterial;
  private matCrystal?: THREE.MeshStandardMaterial;
  private readonly body: THREE.Object3D; // torso (recebe lunge/flash)
  private attackLunge = 0; // avanço visual do corpo durante o golpe (feel por arma)
  private readonly armR = new THREE.Group(); // braço direito (segura a arma / balança no ataque)
  private readonly armL = new THREE.Group(); // braço esquerdo (contra-swing na caminhada)
  private readonly weaponAnchor = new THREE.Group(); // ponto de fixação da arma
  /** 10.6E — cor/brilho de energia da arma na mão (vem da raridade do item equipado). */
  /** 10.6F — cor de destaque da roupa, usada na luz de borda. */
  private outfitRim = 0x9fd6ff;
  private matMetal: THREE.MeshStandardMaterial | null = null;
  /** 10.9BT — cor da CLASSE (reator nas costas + luz de borda). null = usa a energia da arma/roupa. */
  private classColor: number | null = null;
  private reactorMat: THREE.MeshStandardMaterial | null = null;
  /** 10.9BT — gema do cinto: cor/brilho da RARIDADE da arma equipada (setWeaponRarity). */
  private beltGemMat: THREE.MeshStandardMaterial | null = null;
  private weaponEnergy: number | null = null;
  private weaponGlow = 1;
  private weaponMesh: THREE.Object3D | null = null;
  private currentWeaponVisual = "";
  private readonly legL = new THREE.Group();
  private readonly legR = new THREE.Group();
  private walkPhase = 0;
  /** 10.5O — painéis da capa/cachecol: abrem para trás conforme a velocidade (peso e vida). */
  private readonly cloakParts: THREE.Object3D[] = [];
  private cloakLift = 0;
  private cloakLiftHeavy = 0; // 10.9BT: painel central (mais pesado, atrasa)

  // ---- Pipeline de modelos (GLB rigado) — null enquanto não há GLB (→ rig procedural) ----
  private readonly rigged: RiggedVisual | null;
  // armadura VISÍVEL no Hunter procedural: âncoras por slot (seguem os membros) + gerenciador
  private readonly armorAnchors: Partial<Record<EquipmentSlot, THREE.Object3D[]>> = {};
  private hunterEquipment: HunterEquipment | null = null;

  constructor(
    scene: THREE.Scene,
    private readonly camera: THREE.PerspectiveCamera,
    private readonly input: GameInput,
    private readonly arena: Arena,
  ) {
    this.health = new HealthComponent(this.cfg.maxHp, undefined, () => this.die());
    this.health.setLethalGuard(() => this.tryUnbreakable());
    this.combatStats = {
      baseDamage: this.cfg.baseDamage,
      critChance: this.cfg.critChance,
      critDamageMult: this.cfg.critDamageMult,
      armor: this.cfg.armor,
    };

    this.bodyMat = new THREE.MeshStandardMaterial({
      color: 0x6a5bd6,
      emissive: 0x3a1d80,
      emissiveIntensity: 0.5,
      roughness: 0.55,
      metalness: 0.2,
      flatShading: true,
    });
    // GLB rigado do Hunter, se disponível; senão, rig procedural (fallback, §12).
    this.rigged = createPlayerVisual({ color: 0x6a5bd6, emissive: 0x6a3bff, emissiveIntensity: 0.5 });
    if (this.rigged) {
      this.body = this.rigged.root;
      this.group.add(this.rigged.root);
      attachHunterIdentity(this.rigged); // identidade VOID (visor/cristais) presa aos bones
      this.rigged.anim.play("Idle", { crossfade: 0 });
    } else {
      this.body = this.buildHunter();
      this.group.add(this.body);
      this.hunterEquipment = new HunterEquipment(this.armorAnchors); // armadura visível
    }

    const glow = new THREE.PointLight(0x7c4dff, 3, 9, 2); // 10.9BQ: era 7/12 — duas luzes no herói estouravam o hub
    glow.position.set(0, 1.4, 0);
    this.group.add(glow);

    this.group.position.set(0, 0, 0);
    scene.add(this.group);
    this.setWeaponVisual("fist");
  }

  /** 10.4Z — aplica uma ROUPA (recolore os materiais do Hunter). Cosmético. */
  applyOutfit(o: { armor: number; armorEmissive: number; cloak: number; accent: number; accentEmissive: number; crystal: number; crystalEmissive: number }): void {
    this.bodyMat.color.setHex(o.armor);
    this.bodyMat.emissive.setHex(o.armorEmissive); // 10.5A: emissive combina com a roupa (sem "pink")
    this.matDark?.color.setHex(o.cloak);
    if (this.matAccent) {
      this.matAccent.color.setHex(o.accent);
      this.matAccent.emissive.setHex(o.accentEmissive);
    }
    if (this.matCrystal) {
      this.matCrystal.color.setHex(o.crystal);
      this.matCrystal.emissive.setHex(o.crystalEmissive);
    }
    if (this.rigged) for (const m of this.rigged.bodyMats) m.color.setHex(o.armor);
    // 10.6F — a ROUPA agora se lê de cima. Visto do alto, o que mais aparece é o contorno
    // aceso (luz de borda) e a capa; as duas coisas eram fixas, então trocar de roupa não
    // mudava nada na tela. Agora a borda pega o tom de destaque da roupa.
    this.outfitRim = o.accent;
    this.refreshRim();
  }

  /** Reaplica a luz de borda (chamado ao vestir, ao construir e ao trocar classe/arma).
   * 10.9BT: a borda agora usa a cor da CLASSE (senão a energia da arma; senão o destaque da roupa). */
  private refreshRim(): void {
    const c = this.classColor ?? this.weaponEnergy ?? this.outfitRim;
    applyRim(this.bodyMat, { color: c, power: 2.3, strength: 1.3 });
    if (this.matDark) applyRim(this.matDark, { color: c, power: 2.0, strength: 1.85 });
    if (this.matMetal) applyRim(this.matMetal, { color: 0xdce7ff, power: 2.9, strength: 1.05 });
    if (this.reactorMat) {
      this.reactorMat.color.setHex(c);
      this.reactorMat.emissive.setHex(c);
    }
  }

  /** 10.9BT — cor da classe (Game chama ao escolher/trocar classe). Reator + borda pegam a cor. */
  setClassColor(color: number | null): void {
    if (color === this.classColor) return;
    this.classColor = color;
    this.refreshRim();
  }

  /** Constrói o HUNTER estilizado (10.4C): silhueta afiada — capuz + visor forte, capa fluida,
   * peitoral em camadas, pauldrons angulares e cristais VOID. Grupos animáveis
   * (legL/legR/armR/weaponAnchor) preservados para o gait/ataque procedural. */
  private buildHunter(): THREE.Object3D {
    const root = new THREE.Group();
    addContactShadow(root, this.cfg.radius * 1.9, 0.55); // sombra de contato (não "flutua")
    // 10.6B — LUZ DO HERÓI. Uma lanterna quente presa ao Caçador: acende ele e o chão em
    // volta, então o boneco é sempre o ponto mais claro da tela e nunca se perde no bioma
    // (o problema era pior nos territórios roxos/escuros e à noite).
    // 10.9BQ — luz "de leitura" do personagem: 1.55/9 → 1.0/7 (o hub tem uma escala de luz única:
    // núcleo do Santuário + esta; portais/altar só emissivo).
    const halo = new THREE.PointLight(0xffd7a3, 1.0, 7, 2);
    halo.position.set(0, 1.55, 0);
    root.add(halo);
    const skin = this.bodyMat; // armadura primária (violeta escuro; recebe flash de dano)
    // 10.9GV — relevo (couro/tecido) no corpo e na capa: só bump, sem mudar a cor
    skin.bumpMap = skinTexture("hide", 3); skin.bumpScale = 0.014; skin.needsUpdate = true;
    const dark = new THREE.MeshStandardMaterial({ color: 0x1c1930, roughness: 0.92, metalness: 0.14, flatShading: true, bumpMap: skinTexture("hide", 4), bumpScale: 0.018 });
    const metal = new THREE.MeshStandardMaterial({ color: 0x565274, roughness: 0.45, metalness: 0.65, flatShading: true });
    const accent = new THREE.MeshStandardMaterial({ color: 0x9d7bff, emissive: 0x6a3bff, emissiveIntensity: 1.15, roughness: 0.35, flatShading: true });
    const crystal = new THREE.MeshStandardMaterial({ color: 0x8fe8ff, emissive: 0x39b8ff, emissiveIntensity: 1.5, roughness: 0.25, metalness: 0.2, flatShading: true });
    this.matDark = dark; this.matAccent = accent; this.matCrystal = crystal; // 10.4Z: recoloríveis (roupas)
    // 10.6E — LUZ DE BORDA no Caçador. Ele era uma mancha escura sobre chão escuro. Agora a
    // beirada de cada peça acende sozinha (fresnel no emissivo), então a silhueta se descola
    // do cenário em qualquer bioma e à noite — sem contorno e sem draw call novo.
    this.matMetal = metal;

    // ---- QUADRIL + cinto de energia ----
    const hips = new THREE.Mesh(new THREE.BoxGeometry(0.48, 0.26, 0.32), dark);
    hips.position.y = 0.66; hips.castShadow = true; root.add(hips);
    const belt = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.08, 0.34), metal);
    belt.position.y = 0.72; root.add(belt);
    // 10.9BT — gema do cinto com material próprio: cor da RARIDADE da arma equipada
    this.beltGemMat = new THREE.MeshStandardMaterial({ color: 0x8fe8ff, emissive: 0x39b8ff, emissiveIntensity: 0.9, roughness: 0.25, metalness: 0.2, flatShading: true });
    const beltGem = new THREE.Mesh(new THREE.OctahedronGeometry(0.07, 0), this.beltGemMat);
    beltGem.position.set(0, 0.72, 0.19); root.add(beltGem);

    // ---- PERNAS (grupos p/ gait): coxa + canela + joelheira + pé ----
    for (const [g0, lx] of [[this.legL, -0.14], [this.legR, 0.14]] as [THREE.Group, number][]) {
      const thigh = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.36, 0.22), skin);
      thigh.position.y = -0.18; thigh.castShadow = true; g0.add(thigh);
      const knee = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.1, 0.22), metal);
      knee.position.set(0, -0.37, 0.02); g0.add(knee);
      const shin = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.36, 0.18), dark);
      shin.position.set(0, -0.54, 0.02); shin.castShadow = true; g0.add(shin);
      const foot = new THREE.Mesh(new THREE.BoxGeometry(0.19, 0.12, 0.36), metal);
      foot.position.set(0, -0.71, 0.1); foot.castShadow = true; g0.add(foot);
      g0.position.set(lx, 0.64, 0);
      root.add(g0);
    }

    // ---- TORSO afilado + peitoral em camadas ----
    const torso = new THREE.Mesh(new THREE.CylinderGeometry(0.28, 0.42, 0.74, 10), skin);
    torso.position.y = 1.06; torso.castShadow = true; root.add(torso);
    const abdomen = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.3, 0.3), dark);
    abdomen.position.y = 0.84; root.add(abdomen);
    // placa peitoral (metal) inclinada + núcleo de cristal
    const plate = new THREE.Mesh(new THREE.BoxGeometry(0.44, 0.4, 0.16), metal);
    plate.position.set(0, 1.16, 0.24); plate.rotation.x = -0.12; plate.castShadow = true; root.add(plate);
    const core = new THREE.Mesh(new THREE.OctahedronGeometry(0.11, 0), crystal);
    core.position.set(0, 1.16, 0.34); root.add(core);

    // ---- CAPA em 3 PAINÉIS (10.9BT: era cone + 2 painéis — o cone lia como saia rígida).
    // Cada painel gira a partir do OMBRO (pivô no topo), então abre pra trás como pano de
    // verdade; o central é mais largo/longo e tem "peso" (atrasa em relação aos laterais). ----
    const panelGeo = new THREE.BoxGeometry(0.3, 1.02, 0.05);
    for (const [cx, w, h] of [[-0.22, 0.3, 1.0], [0, 0.34, 1.12], [0.22, 0.3, 1.0]] as const) {
      const pivot = new THREE.Group();
      pivot.position.set(cx, 1.42, -0.2);
      const panel = new THREE.Mesh(panelGeo, dark);
      panel.scale.set(w / 0.3, h / 1.02, 1);
      panel.position.set(0, -h / 2, 0);
      panel.castShadow = true;
      pivot.add(panel);
      root.add(pivot);
      this.cloakParts.push(pivot); // 10.5O: balança ao correr (índices 0..2 = painéis da capa)
    }

    // ---- OMBROS: canga + pauldrons angulares + cristais ----
    const yoke = new THREE.Mesh(new THREE.BoxGeometry(0.8, 0.2, 0.4), metal);
    yoke.position.y = 1.4; yoke.castShadow = true; root.add(yoke);
    for (const sx of [-0.46, 0.46]) {
      const pald = new THREE.Mesh(new THREE.ConeGeometry(0.2, 0.3, 4), skin);
      pald.position.set(sx, 1.44, 0); pald.rotation.z = sx < 0 ? 0.5 : -0.5; pald.rotation.y = Math.PI / 4;
      pald.castShadow = true; root.add(pald);
      const cry = new THREE.Mesh(new THREE.OctahedronGeometry(0.08, 0), crystal);
      cry.position.set(sx, 1.56, 0); root.add(cry);
    }
    // gola + pontas de cachecol atrás (fluidez)
    const collar = new THREE.Mesh(new THREE.TorusGeometry(0.18, 0.06, 6, 12), accent);
    collar.rotation.x = Math.PI / 2; collar.position.y = 1.54; root.add(collar);
    for (const sx of [-0.1, 0.1]) {
      const tail = new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.5, 0.04), accent);
      tail.position.set(sx, 1.3, -0.22); tail.rotation.x = 0.3; root.add(tail);
      this.cloakParts.push(tail); // 10.5O: ponta do cachecol acompanha o movimento
    }

    // ---- CABEÇA encapuzada + visor forte ----
    const head = new THREE.Mesh(new THREE.SphereGeometry(0.2, 10, 8), dark);
    head.position.y = 1.72; head.castShadow = true; root.add(head);
    const hood = new THREE.Mesh(new THREE.ConeGeometry(0.3, 0.46, 6), skin);
    hood.position.y = 1.9; hood.rotation.y = Math.PI / 6; hood.castShadow = true; root.add(hood);
    const hoodBack = new THREE.Mesh(new THREE.ConeGeometry(0.24, 0.44, 5), dark);
    hoodBack.position.set(0, 1.86, -0.16); hoodBack.rotation.x = 0.55; root.add(hoodBack);
    // visor: dupla barra de energia ciano (leitura de direção + identidade, sem rosto)
    const visor = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.08, 0.05), crystal);
    visor.position.set(0, 1.72, 0.19); visor.rotation.x = 0.1; root.add(visor);
    const visor2 = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.04, 0.05), crystal); // 2ª barra (identidade)
    visor2.position.set(0, 1.62, 0.19); visor2.rotation.x = 0.1; root.add(visor2);
    const brow = new THREE.Mesh(new THREE.OctahedronGeometry(0.05, 0), crystal);
    brow.position.set(0, 1.84, 0.12); root.add(brow);
    // 10.5E: "reator" de cristal nas costas + emblema no peito (mais cara de jogo)
    // 10.9BT: o reator tem material próprio e PULSA na cor da classe (ver updateAttackVisual)
    this.reactorMat = new THREE.MeshStandardMaterial({ color: 0x8fe8ff, emissive: 0x39b8ff, emissiveIntensity: 1.0, roughness: 0.25, metalness: 0.2, flatShading: true });
    const backShard = new THREE.Mesh(new THREE.OctahedronGeometry(0.16, 0), this.reactorMat);
    backShard.scale.set(0.7, 1.3, 0.5); backShard.position.set(0, 1.22, -0.24); root.add(backShard);
    const reactorRing = new THREE.Mesh(new THREE.TorusGeometry(0.12, 0.025, 5, 10), metal);
    reactorRing.position.set(0, 1.22, -0.23); root.add(reactorRing);
    const emblem = new THREE.Mesh(new THREE.OctahedronGeometry(0.07, 0), accent);
    emblem.scale.set(0.6, 1, 0.3); emblem.position.set(0, 1.0, 0.28); root.add(emblem);

    // ---- BRAÇOS: upper + forearm + hand (esq fixo; dir = grupo que segura a arma) ----
    const upperGeo = new THREE.BoxGeometry(0.15, 0.34, 0.16);
    const foreGeo = new THREE.BoxGeometry(0.13, 0.3, 0.14);
    const handGeo = new THREE.BoxGeometry(0.15, 0.14, 0.16);
    const upL = new THREE.Mesh(upperGeo, skin); upL.position.y = -0.16; upL.castShadow = true; this.armL.add(upL);
    const foreL = new THREE.Mesh(foreGeo, dark); foreL.position.set(0, -0.44, 0.04); foreL.castShadow = true; this.armL.add(foreL);
    const handL = new THREE.Mesh(handGeo, metal); handL.position.set(0, -0.62, 0.06); this.armL.add(handL);
    const guardL = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.14, 0.17), metal); guardL.position.set(0, -0.5, 0.05); this.armL.add(guardL);
    this.armL.position.set(-0.45, 1.36, 0.06); this.armL.rotation.z = 0.12; this.armL.rotation.x = 0.25; root.add(this.armL);
    // braço direito (segura a arma)
    const upR = new THREE.Mesh(upperGeo, skin); upR.position.y = -0.16; upR.castShadow = true; this.armR.add(upR);
    const foreR = new THREE.Mesh(foreGeo, dark); foreR.position.set(0, -0.44, 0.04); foreR.castShadow = true; this.armR.add(foreR);
    const guardR = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.14, 0.17), metal); guardR.position.set(0, -0.5, 0.05); this.armR.add(guardR);
    const handR = new THREE.Mesh(handGeo, metal); handR.position.set(0, -0.6, 0.06); this.armR.add(handR);
    this.armR.position.set(0.45, 1.36, 0.1);
    this.armR.rotation.x = 0.5; // levemente à frente
    this.weaponAnchor.position.set(0, -0.64, 0.12); // na "mão"
    this.armR.add(this.weaponAnchor);
    root.add(this.armR);

    // âncoras de ARMADURA VISÍVEL (seguem os membros): cabeça/peito/acessório na raiz,
    // luvas nas mãos, botas nos pés. HunterEquipment monta/troca as peças aqui.
    const anchorAt = (parent: THREE.Object3D, x: number, y: number, z: number): THREE.Group => {
      const a = new THREE.Group();
      a.position.set(x, y, z);
      parent.add(a);
      return a;
    };
    this.armorAnchors.head = [anchorAt(root, 0, 1.72, 0)];
    this.armorAnchors.chest = [anchorAt(root, 0, 1.12, 0)];
    this.armorAnchors.accessory = [anchorAt(root, 0, 1.5, 0.08)];
    this.armorAnchors.gloves = [anchorAt(this.armL, 0, -0.6, 0.06), anchorAt(this.armR, 0, -0.6, 0.06)];
    this.armorAnchors.boots = [anchorAt(this.legL, 0, -0.68, 0.06), anchorAt(this.legR, 0, -0.68, 0.06)];

    this.refreshRim(); // 10.9BT: depois do reator existir (a borda e o reator pegam a mesma cor)
    return root;
  }

  /**
   * 10.6E — RARIDADE DA ARMA NA MÃO. O veio de energia da arma agora usa a cor da
   * raridade e queima mais forte quanto mais rara. Chamado pelo Game ao recalcular o
   * equipamento; reconstrói o mesh só quando a cor realmente muda.
   */
  setWeaponRarity(energy: number | null, glow: number, tier = 1, family = ""): void {
    if (energy === this.weaponEnergy && Math.abs(glow - this.weaponGlow) < 0.01 && tier === this.weaponTier && family === this.weaponFamily) return;
    this.weaponEnergy = energy;
    this.weaponGlow = glow;
    this.weaponTier = tier; // 10.9GX
    this.weaponFamily = family; // 10.9GZ
    // 10.9BT — a gema do cinto acompanha a raridade da arma (teto 1.2: bloom threshold 0.85)
    if (this.beltGemMat) {
      const c = energy ?? 0x39b8ff;
      this.beltGemMat.color.setHex(c);
      this.beltGemMat.emissive.setHex(c);
      this.beltGemMat.emissiveIntensity = Math.min(1.2, 0.7 + glow * 0.25);
    }
    if (this.classColor === null) this.refreshRim(); // sem classe: a borda segue a energia da arma
    const v = this.currentWeaponVisual;
    this.currentWeaponVisual = ""; // força reconstrução com a cor nova
    this.setWeaponVisual(v);
  }

  private weaponTier = 1; // 10.9GX
  private weaponFamily = ""; // 10.9GZ
  private get weaponLook(): WeaponLook {
    return { energy: this.weaponEnergy ?? undefined, glow: this.weaponGlow, tier: this.weaponTier, family: this.weaponFamily || undefined };
  }

  /** Constrói/troca a arma VISÍVEL na mão conforme o visualId da arma equipada. */
  setWeaponVisual(visualId: string): void {
    if (visualId === this.currentWeaponVisual) return;
    this.currentWeaponVisual = visualId;
    // rig GLB: a arma é um mesh real preso ao socket RightHand (§4) — troca ao equipar,
    // com offset por arma (§19) para alinhar à mão.
    if (this.rigged) {
      this.rigged.sockets.attach("RightHand", buildWeaponMesh(visualId, this.weaponLook), weaponSocketOffset(visualId));
      return;
    }
    if (this.weaponMesh) {
      this.weaponAnchor.remove(this.weaponMesh);
      this.weaponMesh.traverse((o) => {
        if (o instanceof THREE.Mesh) o.geometry.dispose();
      });
    }
    this.weaponMesh = buildWeaponMesh(visualId, this.weaponLook);
    this.weaponAnchor.add(this.weaponMesh);
  }

  /** Atualiza a ARMADURA visível no Hunter (peças low-poly nos membros) conforme o equipado. */
  setEquipmentVisual(items: readonly (EquipmentInstance | null)[]): void {
    this.hunterEquipment?.update(items);
  }

  // ---- Damageable ----
  get position(): THREE.Vector3 {
    return this.group.position;
  }
  get isDead(): boolean {
    return this.state === "dead";
  }
  /** 10.9CT — DEV god mode: força invulnerabilidade permanente (só usado pelo painel DEV). */
  setDevInvuln(v: boolean): void { this._invuln = v; }
  /** 10.9CX — i-frames curtos (investida): usa o mesmo canal da proteção pós-respawn. */
  grantInvuln(sec: number): void { this.spawnProtect = Math.max(this.spawnProtect, sec); }
  get invulnerable(): boolean {
    // imunidade central da safe zone: cobre melee, projéteis, AoE e efeitos futuros
    // (o DamageSystem lê `invulnerable` no topo). Só na safe zone — NÃO no buffer.
    return this._invuln || this.spawnProtect > 0 || this.isDead || this._inSanctuary;
  }
  /** Combate liberado? (false na safe zone e no buffer). */
  get combatEnabled(): boolean {
    return this._combatEnabled;
  }
  /** Dentro da safe zone (imune/cura/desengaja inimigos)? */
  get inSanctuary(): boolean {
    return this._inSanctuary;
  }
  /** Zona atual do Player. */
  get combatZone(): SanctuaryZone {
    return this.zone;
  }
  /**
   * Atualiza a zona de combate a partir da geometria da Sanctuary (histerese):
   * o combate só LIGA quando o Player sai TOTALMENTE do buffer, e DESLIGA assim que
   * ele cruza de volta para o buffer/safe zone. Retorna a zona atual.
   */
  updateZone(sanctuary: Sanctuary): SanctuaryZone {
    const z = sanctuary.zoneOf(this.group.position);
    this._inSanctuary = z === "sanctuary";
    if (this._combatEnabled) {
      if (z !== "combat") this._combatEnabled = false;
    } else if (z === "combat") {
      this._combatEnabled = true;
    }
    this.zone = z;
    return z;
  }
  get armor(): number {
    return this.combatStats.armor;
  }
  /** Redução de dano recebido (Void Resilience + janela do Void Step). */
  /** 10.9EW — reducao de dano da CLASSE (Pele de Aco do guerreiro). NAO passa pela Ameaca. */
  private classDR = 0;
  setClassDamageReduction(v: number): void { this.classDR = Math.max(0, Math.min(0.6, v)); }
  get classDamageReduction(): number { return this.classDR; } // 10.9EW — DamageSystem aplica depois do piso
  get damageReduction(): number {
    let r = this.resilience;
    if (this.voidStepTimer > 0) r += GameConfig.voidTree.voidStep.reductionPct;
    return Math.min(0.8, r);
  }
  /** 10.5K: defesa por tipo de dano (lida pelo DamageSystem). */
  get typeDefense(): Partial<Record<DamageType, number>> {
    return this.typeDef;
  }
  /** 10.5K: chance de bloquear (lida pelo DamageSystem). */
  get blockChance(): number {
    return this._blockChance;
  }
  /** 10.5K: regeneração contínua em HP/s (para a tela de atributos). */
  get regenPerSecond(): number {
    return this.regenPct * this.health.max;
  }
  /** Velocidade de movimento atual (upgrades + sprint + Predator). */
  get speed(): number {
    const predator = this.predatorTimer > 0 ? 1 + GameConfig.voidTree.predator.moveBuffPct : 1;
    return this.moveSpeed * (this.sprintTimer > 0 ? this.cfg.sprint.speedMult : 1) * predator;
  }
  /** Está correndo (sprint ativo)? */
  get sprinting(): boolean {
    return this.sprintTimer > 0;
  }
  /** Velocidade base (sem sprint) — para a Character Screen. */
  get moveSpeedBase(): number {
    return this.moveSpeed;
  }
  get sprintDurationValue(): number {
    return this.effSprintDuration;
  }
  get sprintCooldownValue(): number {
    return this.effSprintCooldown;
  }
  /** Mods de hook para o HookSystem (Void Tree). */
  get hookRangeMult(): number {
    return this.effHookRangeMult;
  }
  get hookCdReduc(): number {
    return this.effHookCdReduc;
  }
  /** Notifica um abate (Void Dominance / Predator). */
  notifyKill(): void {
    if (this.traits.has("predator")) this.predatorTimer = GameConfig.voidTree.predator.duration;
    if (this.traits.has("voidDominance")) {
      const d = GameConfig.voidTree.dominance;
      this.domKills.push(this.clock);
      this.domKills = this.domKills.filter((t) => this.clock - t <= d.window);
      if (this.domKills.length >= d.kills) {
        this.domBuffTimer = d.duration;
        this.domKills.length = 0;
      }
    }
  }

  onHit(): void {
    if (this.isDead) return;
    this.hitStun = 0.14;
    this.flashTimer = 0.14;
    this.sinceHit = 0; // reinicia o gate de regeneração da base
    if (this.state !== "sprinting") this.state = "hit";
    // reação de dano como camada aditiva curta — NÃO rota o Player nem trava o joystick (§17).
    this.rigged?.anim.playAdditiveOnce("Hit", { timeScale: 1.3 });
  }

  applyKnockback(dir: THREE.Vector3, force: number): void {
    this.knockback.addScaledVector(dir, force);
  }

  // ---- loop ----
  /** `target` = alvo do AutoTarget (auto-attack); `enemies` = todos os alvos (cleave/arc). */
  update(dt: number, target: Damageable | null, enemies: readonly Damageable[]): void {
    if (this.isDead) return;

    this.currentTarget = target && !target.isDead ? target : null;
    this.clock += dt;
    this.sprintCooldown = Math.max(0, this.sprintCooldown - dt);
    this.attackCooldown = Math.max(0, this.attackCooldown - dt);
    this.hitStun = Math.max(0, this.hitStun - dt);
    this.sinceHit += dt;
    if (this.spawnProtect > 0) this.spawnProtect -= dt;
    // 10.5K/10.7G: regeneração contínua. ANTI-IMORTALIDADE:
    //  · regenPct é CAPADO em maxRegenPerSecPct do HP máx (era 6% com 83K HP = 4978/s = imortal)
    //  · EM COMBATE (tomou dano < 4s) a regen cai à METADE — sustain entre lutas, não invencível
    if (this.health.current < this.health.max) {
      const cc = GameConfig.combat;
      const cap = this.health.max * (cc.maxRegenPerSecPct ?? 0.02);
      const pctRegen = Math.min(this.regenPct * this.health.max, cap);
      const inCombat = this.sinceHit < 4;
      const combatMult = inCombat ? 0.5 : 1;
      const total = (pctRegen + this.flatRegenPerSec) * combatMult;
      if (total > 0) this.health.heal(total * dt);
    }
    // Void Tree: timers dos nós especiais
    this.unbreakableCd = Math.max(0, this.unbreakableCd - dt);
    this.secondWindCd = Math.max(0, this.secondWindCd - dt);
    this.voidStepTimer = Math.max(0, this.voidStepTimer - dt);
    this.predatorTimer = Math.max(0, this.predatorTimer - dt);
    this.domBuffTimer = Math.max(0, this.domBuffTimer - dt);
    // Second Wind: HP baixo -> cura com cooldown
    if (this.traits.has("secondWind") && this.secondWindCd <= 0) {
      const sw = GameConfig.voidTree.secondWind;
      if (this.health.current / this.health.max <= sw.threshold) {
        this.health.heal(this.health.max * sw.healPct);
        this.secondWindCd = sw.cooldown;
      }
    }
    if (this.flashTimer > 0) {
      this.flashTimer -= dt;
      const ei = this.flashTimer > 0 ? 2.2 : 0.6;
      this.bodyMat.emissiveIntensity = ei;
      if (this.rigged) for (const m of this.rigged.bodyMats) m.emissiveIntensity = this.flashTimer > 0 ? 2.2 : 0.35;
    }

    this.readMoveInput();
    this.updateSprint(dt);
    this.tryAutoAttack(dt, enemies);
    this.updateMovement(dt);

    this.integrate(dt);
    this.updateFacing(dt);
    this.updateStateLabel();
    this.rigged?.update(dt); // avança o AnimationMixer (no-op no fallback procedural)
    this.updateAttackVisual(dt);
    this.group.rotation.y = this.facingAngle;
  }

  /** Animação procedural do hunter: lunge no golpe + gait das pernas + swing do braço/arma
   * + idle breathe + bob e lean de corrida (§13-§16). */
  private updateAttackVisual(dt: number): void {
    if (this.rigged) {
      this.updateRiggedAnim();
      return;
    }
    this.attackLunge = approach(this.attackLunge, 0, dt * 3.5);
    this.body.position.z = this.attackLunge;

    // gait: pernas alternadas + bob vertical + lean + contra-swing dos braços
    const speed = Math.hypot(this.velocity.x, this.velocity.z);
    const running = this.sprintTimer > 0;
    const attacking = this.attackPhase !== "none";
    // 10.5O — CAPA: abre para trás conforme a velocidade e ondula (dá peso ao movimento)
    // 10.9BT — 3 painéis (0..2) com PESO: o central (mais longo) atrasa e sobe menos que os laterais;
    // os laterais abrem pra fora. Pontas do cachecol (3..4) continuam como antes.
    if (this.cloakParts.length > 0) {
      const want = Math.min(0.55, speed * 0.08) * (running ? 1.35 : 1);
      this.cloakLift += (want - this.cloakLift) * Math.min(1, dt * 6);
      this.cloakLiftHeavy += (want * 0.8 - this.cloakLiftHeavy) * Math.min(1, dt * 3.2); // painel central: mais lento
      const wave = Math.sin(this.clock * (running ? 9 : 4)) * (0.06 + this.cloakLift * 0.35);
      for (let i = 0; i < this.cloakParts.length; i++) {
        const c = this.cloakParts[i];
        if (i < 3) {
          const side = i === 0 ? -1 : i === 2 ? 1 : 0;
          const lift = side === 0 ? this.cloakLiftHeavy : this.cloakLift;
          const w = side === 0 ? wave * 0.7 : wave;
          c.rotation.x = 0.12 + lift + w; // pivô no ombro: positivo = a barra vai pra trás (-Z)
          c.rotation.z = side * (0.1 + lift * 0.25) + w * 0.2 * side;
          continue;
        }
        const side = i % 2 === 0 ? 1 : -1;
        c.rotation.x = 0.22 + this.cloakLift + wave * 0.6;
        c.rotation.z = 0.04 * side + wave * 0.25 * side;
      }
    }
    // 10.9BT — reator nas costas pulsa na cor da classe (teto 1.2: bloom threshold 0.85)
    if (this.reactorMat) this.reactorMat.emissiveIntensity = 0.85 + Math.sin(this.clock * 3.2) * 0.3;
    if (speed > 0.3) {
      this.walkPhase += dt * (6 + speed * 1.4) * (running ? 1.4 : 1);
      const s = Math.sin(this.walkPhase);
      const amp = Math.min(0.62, speed * 0.13) * (running ? 1.3 : 1);
      this.legL.rotation.x = s * amp;
      this.legR.rotation.x = -s * amp;
      // bob vertical (passada) + inclinação p/ frente (mais forte no sprint)
      this.body.position.y = Math.abs(s) * (running ? 0.07 : 0.05);
      const leanT = Math.min(0.24, speed * 0.032) + (running ? 0.12 : 0);
      this.body.rotation.x += (leanT - this.body.rotation.x) * 0.15;
      // braços: esquerdo contra-oposto à perna esquerda; direito acompanha se não atacando
      this.armL.rotation.x = 0.25 - s * amp * 0.9;
      if (!attacking) this.armR.rotation.x += (0.5 + s * amp * 0.9 - this.armR.rotation.x) * 0.3;
    } else {
      this.legL.rotation.x += (0 - this.legL.rotation.x) * 0.2;
      this.legR.rotation.x += (0 - this.legR.rotation.x) * 0.2;
      // idle: breathe sutil (escala) + settle vertical + endireita o tronco + sway do braço esq
      const breathe = 1 + Math.sin(this.clock * 2) * 0.02;
      this.body.scale.y += (breathe - this.body.scale.y) * 0.1;
      this.body.position.y += (0 - this.body.position.y) * 0.12;
      this.body.rotation.x += (0 - this.body.rotation.x) * 0.12;
      this.armL.rotation.x += (0.25 + Math.sin(this.clock * 1.6) * 0.04 - this.armL.rotation.x) * 0.1;
    }

    // swing do braço da arma: recua+carrega no windup, golpe forte no hit, relaxa na recovery
    let armTarget = 0.5;
    const isBow = this.effWeapon.weaponType === "bow" || this.effWeapon.weaponType === "crossbow" || this.effWeapon.weaponType === "tome"; // 10.9L25
    if (isBow) {
      // 10.9EI — ARCO nao "golpeia". Ele usava o mesmo swing da espada (braco a 1.6 rad no hit):
      // o braco girava 92 graus pra frente e DEITAVA o arco — medido: altura 1.75 parado, 0.21 no
      // disparo. Como o arqueiro ataca o tempo todo, passava o jogo com o arco deitado (a "barra
      // verde"). Agora: braco ESTENDIDO a frente enquanto atira (mirando), sem chicotear.
      if (this.attackPhase) armTarget = 1.35;
    } else if (this.attackPhase === "windup") armTarget = -0.35;
    else if (this.attackPhase === "hit") armTarget = 1.6;
    else if (this.attackPhase === "recovery") armTarget = 0.95;
    this.armR.rotation.x += (armTarget - this.armR.rotation.x) * 0.4;
    // 10.9EI — e o arco fica sempre EM PE: cancela a inclinacao do braco (pai da ancora da arma).
    if (this.weaponMesh) this.weaponMesh.rotation.x = isBow ? -this.armR.rotation.x : 0;
  }

  /** Animação do rig GLB: a locomoção (Idle/Walk/Run/Sprint) toca SEMPRE — inclusive durante
   * o ataque (§10). O ataque entra como camada ADITIVA só no tronco/braços (§11), então as
   * pernas continuam correndo. timeScale por clip reduz foot-sliding (§14). */
  private updateRiggedAnim(): void {
    const r = this.rigged!;
    const speed = Math.hypot(this.velocity.x, this.velocity.z);
    const running = this.sprintTimer > 0;
    const loco: AnimState = running ? "Sprint" : speed > this.moveSpeed * 0.62 ? "Run" : speed > 0.3 ? "Walk" : "Idle";
    // casa a cadência do clip com a velocidade real (menos deslize dos pés)
    let ts = 1;
    if (loco === "Walk") ts = clamp(speed / (this.moveSpeed * 0.55), 0.75, 1.5);
    else if (loco === "Run") ts = clamp(speed / (this.moveSpeed * 1.0), 0.8, 1.5);
    else if (loco === "Sprint") ts = clamp(speed / (this.moveSpeed * this.cfg.sprint.speedMult), 0.85, 1.4);
    r.anim.play(loco, { crossfade: 0.22, timeScale: ts });
  }

  /** Estado de ataque conforme o tipo de arma (para o clip correto no rig). */
  private attackAnimState(): AnimState {
    switch (this.effWeapon.weaponType) {
      case "axe":
        return "AxeAttack";
      case "bow":
        return "BowDraw";
      case "staff":
      case "tome": // 10.9L25 — grimório também canaliza/lança à distância
        return "BowDraw"; // 10.9AD — cajado usa a pose de canalizar/lançar à distância
      case "crossbow": // 10.9L25 — besta também mira/dispara à distância
        return "BowDraw";
      default:
        return "SwordAttack";
    }
  }

  /** timeScale do clip de ataque por arma: machado pesado/lento, lança rápida (§5/§6). */
  private attackTimeScale(): number {
    switch (this.effWeapon.weaponType) {
      case "axe":
        return 0.7;
      case "bow":
      case "crossbow": // 10.9L25
        return 0.9;
      default:
        return 1.0;
    }
  }

  /** Pose de arremesso do Hook (§8) — camada aditiva; a MECÂNICA do Hook não muda. */
  playHookThrow(): void {
    this.rigged?.anim.playAdditiveOnce("HookThrow", { timeScale: 1.15 });
  }

  private readMoveInput(): void {
    const mv = this.input.move; // joystick (mobile) ou WASD (dev)
    // direções relativas à câmera, projetadas no chão
    this.camera.getWorldDirection(_camF);
    _camF.y = 0;
    _camF.normalize();
    _camR.set(-_camF.z, 0, _camF.x); // 90° à direita
    const fwd = -mv.y; // frente = para cima no joystick / W
    const right = mv.x; // direita = D / joystick p/ direita
    _move.set(0, 0, 0).addScaledVector(_camF, fwd).addScaledVector(_camR, right);
    if (_move.lengthSq() > 1) _move.normalize(); // diagonal não acelera
  }

  private updateMovement(dt: number): void {
    const hasInput = _move.lengthSq() > 0.0001;
    const speed = this.speed; // inclui sprint
    const rate = (hasInput ? this.cfg.accel : this.cfg.decel) * dt;
    const targetX = hasInput ? _move.x * speed : 0;
    const targetZ = hasInput ? _move.z * speed : 0;
    this.velocity.x = approach(this.velocity.x, targetX, rate);
    this.velocity.z = approach(this.velocity.z, targetZ, rate);
    // movimentação do usuário > animação de ataque: SEM travar o personagem no ataque.
  }

  // ---- Sprint (corrida): rápido, controlável, com cooldown; NÃO é dash em linha reta ----
  private updateSprint(dt: number): void {
    if (this.sprintTimer > 0) {
      this.sprintTimer -= dt;
      return;
    }
    if (this.input.sprintPressed() && this.sprintCooldown <= 0) {
      this.sprintTimer = this.effSprintDuration;
      this.sprintCooldown = this.effSprintCooldown;
      if (this.traits.has("voidStep")) this.voidStepTimer = GameConfig.voidTree.voidStep.duration;
      events.emit("dash:used", undefined); // reaproveita o evento de feedback
    }
  }

  // ---- Auto-attack: ataca automaticamente o alvo do AutoTarget quando no alcance ----
  private tryAutoAttack(dt: number, enemies: readonly Damageable[]): void {
    if (
      this.attackPhase === "none" &&
      this._combatEnabled && // safe zone / buffer: nenhum ataque novo começa
      this.attackCooldown <= 0 &&
      this.hitStun <= 0 &&
      this.currentTarget &&
      this.inWeaponRange(this.currentTarget)
    ) {
      this.startAttack();
    }
    if (this.attackPhase === "none") return;

    this.attackPhaseTimer -= dt;
    const a = this.cfg.attack;
    const feel = this.weaponFeel;
    if (this.attackPhase === "windup" && this.attackPhaseTimer <= 0) {
      this.attackPhase = "hit";
      this.attackPhaseTimer = a.hitframe;
      this.attackHitDone = false;
      this.attackLunge = feel.lunge; // avanço/recuo visual no instante do golpe
    }
    if (this.attackPhase === "hit") {
      if (!this.attackHitDone) {
        this.performAttack(enemies);
        this.attackHitDone = true;
      }
      if (this.attackPhaseTimer <= 0) {
        this.attackPhase = "recovery";
        this.attackPhaseTimer = a.recovery * feel.recoveryScale;
      }
    }
    if (this.attackPhase === "recovery" && this.attackPhaseTimer <= 0) {
      this.attackPhase = "none";
    }
  }

  /** Arma corpo-a-corpo equipada? (melee não alcança criaturas voando). */
  get meleeEquipped(): boolean {
    return this.effWeapon.attackType === "melee";
  }
  /** Alvo voando fora do alcance de melee? (Bow acerta; Hook derruba.) */
  private meleeBlocked(t: Damageable): boolean {
    return this.meleeEquipped && (t as { flying?: boolean }).flying === true;
  }

  /** Alvo dentro do alcance da arma equipada (e alcançável: melee não pega voador). */
  inWeaponRange(t: Damageable): boolean {
    if (this.meleeBlocked(t)) return false;
    const dx = t.position.x - this.group.position.x;
    const dz = t.position.z - this.group.position.z;
    const r = this.effWeapon.attackRange;
    return dx * dx + dz * dz <= r * r;
  }

  /** Ajustes de "feel" por tipo de arma (só animação/lunge; nunca dano/cadência). */
  private get weaponFeel(): { windupScale: number; recoveryScale: number; lunge: number } {
    return GameConfig.player.weaponFeel[this.effWeapon.weaponType] ?? { windupScale: 1, recoveryScale: 1, lunge: 0.15 };
  }

  private startAttack(): void {
    this.attackPhase = "windup";
    this.attackPhaseTimer = this.cfg.attack.windup * this.weaponFeel.windupScale;
    this.attackCooldown = 1 / this.effWeapon.attacksPerSecond;
    // mira o alvo atual; fallback = movimento; fallback = facing
    const t = this.currentTarget;
    if (t) {
      _aim.subVectors(t.position, this.group.position);
      this.aimAngle = Math.atan2(_aim.x, _aim.z);
    } else if (_move.lengthSq() > 0.0001) {
      this.aimAngle = Math.atan2(_move.x, _move.z);
    }
    // ataque como camada aditiva (tronco/braços) — não trava a corrida (§10/§11).
    if (this.rigged) this.rigged.anim.playAdditiveOnce(this.attackAnimState(), { timeScale: this.attackTimeScale() });
  }

  /** Multiplicador de dano condicional da Void Tree (Dominance + vs Elite + Execute). */
  private attackMult(target: Damageable): number {
    let m = this.domBuffTimer > 0 ? 1 + GameConfig.voidTree.dominance.buffPct : 1;
    if (this.vsEliteMult > 0 && isEliteOrBoss(target)) m *= 1 + this.vsEliteMult;
    if (this.executeTreeMult > 0 && target.health.fraction < GameConfig.mastery.executionerThreshold) m *= 1 + this.executeTreeMult;
    return m;
  }

  /** Aplica o golpe conforme o tipo de arma (MELEE = arco/cleave; RANGED = projétil). */
  private performAttack(enemies: readonly Damageable[]): void {
    // 2ª camada anti-exploit: mesmo se um golpe estava em andamento, nenhum dano/projétil
    // sai dentro da safe zone/buffer (a animação pode terminar, o dano não acontece).
    if (!this._combatEnabled) return;
    if (this.effWeapon.attackType === "ranged") this.performRanged();
    else this.performMelee(enemies);
  }

  private performMelee(enemies: readonly Damageable[]): void {
    const w = this.effWeapon;
    const dirX = Math.sin(this.aimAngle);
    const dirZ = Math.cos(this.aimAngle);
    // 10.6E — o golpe agora DESENHA. Arco de corte no chão, na abertura e no alcance reais
    // da arma, na cor de energia dela (que vem da raridade). Sai mesmo sem acertar nada:
    // o jogador vê o que a arma faz, e não só faíscas quando por acaso conecta.
    events.emit("fx:slash", {
      x: this.group.position.x, z: this.group.position.z, angle: this.aimAngle,
      arc: w.arc, range: w.attackRange, color: this.weaponEnergy ?? 0x8fe8ff,
      heavy: w.weaponType === "axe" || w.weaponType === "mace",
    });
    const cosHalfArc = Math.cos(w.arc / 2);
    const range = w.attackRange;
    const m = GameConfig.mastery;

    // Void Edge (sword): a cada N ataques, o golpe é reforçado
    this.meleeHitCount++;
    let edge = 1;
    if (this.traits.has("voidEdge") && this.meleeHitCount % m.voidEdgeEvery === 0) edge = 1 + m.voidEdgeBonus;
    const executioner = this.traits.has("executioner");
    // nº de alvos por golpe: CLEAVE (machado) = todos no arco; IMPALER/PIERCE (lança) = 2; senão 1
    // 10.9EU — "cleave3" = golpe em area NATO do guerreiro (ate 3 no arco)
    const maxTargets = this.traits.has("cleave") ? 99 : this.traits.has("cleave3") ? 3 : this.traits.has("impaler") || this.traits.has("pierce") ? 2 : 1;

    // reúne candidatos no arco/alcance
    _hits.length = 0;
    for (const enemy of enemies) {
      if (enemy.isDead) continue;
      if ((enemy as { flying?: boolean }).flying === true) continue; // melee não acerta voando
      _to.subVectors(enemy.position, this.group.position);
      _to.y = 0;
      const dist = _to.length();
      if (dist > range + 0.6 || dist < 0.001) continue;
      const dot = (_to.x / dist) * dirX + (_to.z / dist) * dirZ;
      if (dot < cosHalfArc) continue;
      if (wallBetween(this.group.position.x, this.group.position.z, enemy.position.x, enemy.position.z)) continue;
      _hits.push({ e: enemy, d: dist });
    }
    // prioridade: alvo atual primeiro, depois por proximidade
    _hits.sort((a, b) => (a.e === this.currentTarget ? -1 : b.e === this.currentTarget ? 1 : a.d - b.d));

    let hitCount = 0;
    for (const h of _hits) {
      if (hitCount >= maxTargets) break;
      let mult = edge * this.attackMult(h.e);
      if (executioner && h.e.health.fraction < m.executionerThreshold) mult *= 1 + m.executionerBonus;
      const stats = mult === 1 ? this.combatStats : { ...this.combatStats, baseDamage: Math.round(this.combatStats.baseDamage * mult) };
      dealDamage(h.e, stats, {
        targetKind: "enemy", byPlayer: true,
        damageType: w.damageType,
        weaponType: w.weaponType, // 10.6G: fraqueza/resistência por arma
        knockbackFrom: this.group.position,
        knockbackForce: w.knockback,
      });
      hitCount++;
    }
  }

  /** Arma à distância: dispara um PROJÉTIL de verdade (poolado) na direção da mira. */
  private performRanged(): void {
    const w = this.effWeapon;
    if (!this.projectiles) return;
    const m = GameConfig.mastery;
    this.bowShotCount++;
    let radiusScale = 1;
    let dmgMult = 1;
    let pierce = false;
    // Void Shot (bow): a cada N disparos, projétil reforçado que ATRAVESSA alvos
    if (this.traits.has("voidShot") && this.bowShotCount % m.voidShotEvery === 0) {
      radiusScale = 1.6;
      dmgMult = m.voidShotMult;
      pierce = true;
    }
    if (this.currentTarget) dmgMult *= this.attackMult(this.currentTarget);
    const stats: CombatStats = { ...this.combatStats, baseDamage: Math.round(this.combatStats.baseDamage * dmgMult) };
    events.emit("fx:shot", { // 10.6E: clarão curto na boca do arco
      x: this.group.position.x + Math.sin(this.aimAngle) * 0.6,
      y: 1.15,
      z: this.group.position.z + Math.cos(this.aimAngle) * 0.6,
      color: this.weaponEnergy ?? 0x8fe8ff,
    });
    this.projectiles.fire(this.group.position, Math.sin(this.aimAngle), Math.cos(this.aimAngle), w.projectileSpeed, stats, w.damageType, w.knockback, {
      target: this.currentTarget,
      radiusScale,
      pierce,
      weaponType: w.weaponType, // 10.6G
      color: this.weaponEnergy ?? undefined, // 10.9CB — o orbe do cajado sai na cor da arma/classe
    });
  }

  private integrate(dt: number): void {
    this.group.position.addScaledVector(this.velocity, dt);
    this.group.position.addScaledVector(this.knockback, dt);
    // decaimento do knockback
    const decay = Math.exp(-12 * dt);
    this.knockback.multiplyScalar(decay);
    this.arena.clamp(this.group.position, this.cfg.radius);
  }

  private updateFacing(dt: number): void {
    let target = this.facingAngle;
    // atacando: encara o alvo; senão segue o movimento (permite circular o inimigo)
    if (this.attackPhase === "windup" || this.attackPhase === "hit") {
      target = this.aimAngle;
    } else if (_move.lengthSq() > 0.0001) {
      target = Math.atan2(_move.x, _move.z);
    }
    this.facingAngle = dampAngle(this.facingAngle, target, this.cfg.rotationSpeed, dt);
  }

  private updateStateLabel(): void {
    if (this.isDead) return;
    if (this.sprintTimer > 0) this.state = "sprinting";
    else if (this.attackPhase !== "none") this.state = "attacking";
    else if (this.hitStun > 0) this.state = "hit";
    else if (this.velocity.lengthSq() > 0.4) this.state = "walking";
    else this.state = "idle";
  }

  // ---- recálculo central de stats (base + level + altar + equipment + mastery) ----
  /** Injeta o gerenciador de projéteis (armas ranged). */
  setProjectiles(pm: ProjectileManager): void {
    this.projectiles = pm;
  }

  /**
   * Recalcula TODOS os stats efetivos a partir dos mods agregados + arma efetiva + traits.
   * Fonte única de verdade — evita drift (equip/unequip nunca acumula erro).
   */
  recalculate(mods: StatMods, weapon: EffectiveWeapon | null, traits: ReadonlySet<string>): void {
    const ratio = this.health.max > 0 ? this.health.current / this.health.max : 1;
    this.health.max = Math.max(1, Math.round((this.cfg.maxHp + mods.maxHp) * (1 + mods.maxHpMult)));
    this.health.current = Math.min(this.health.max, Math.max(1, Math.round(this.health.max * ratio)));

    this.combatStats.baseDamage = Math.max(1, Math.round((this.cfg.baseDamage + mods.damage) * (1 + mods.damageMult)));
    this.combatStats.critChance = this.cfg.critChance + mods.critChance;
    this.combatStats.critDamageMult = this.cfg.critDamageMult + mods.critDamageMult;
    this.combatStats.armor = this.cfg.armor + mods.armor;
    this.moveSpeed = (this.cfg.moveSpeed + mods.moveSpeed) * (1 + mods.moveSpeedMult);
    this.effWeapon = weapon ?? unarmedWeapon();
    this.traits = traits;
    this.setWeaponVisual(this.effWeapon.visualId); // arma visível na mão
    // Void Tree: hook/sprint efetivos + condicionais
    this.effHookRangeMult = mods.hookRangeMult;
    this.effHookCdReduc = mods.hookCdReduc;
    this.effSprintDuration = this.cfg.sprint.duration * (1 + mods.sprintDurationMult);
    this.effSprintCooldown = this.cfg.sprint.cooldown * (1 - mods.sprintCdReduc);
    this.vsEliteMult = mods.vsEliteMult;
    this.executeTreeMult = mods.executeMult;
    this.resilience = mods.resilience;
    // 10.5K: por tipo de dano (atacante) / defesa por tipo, bloqueio e regeneração (defensor)
    this.combatStats.typeBonus = { slashing: mods.dmgSlashPct, piercing: mods.dmgPiercePct, blunt: mods.dmgBluntPct, arcane: mods.dmgArcanePct }; // 10.9CO — arcano p/ mago/necro
    this.typeDef = { slashing: mods.defSlashPct, piercing: mods.defPiercePct, blunt: mods.defBluntPct };
    this._blockChance = mods.blockChance;
    this.regenPct = mods.regenPct;
  }

  /** Arma efetiva atual (HUD/visual). */
  get weaponInfo(): EffectiveWeapon {
    return this.effWeapon;
  }

  /** Unbreakable: sobrevive a um golpe fatal com 1 HP; entra em cooldown longo. */
  private tryUnbreakable(): boolean {
    if (!this.traits.has("unbreakable") || this.unbreakableCd > 0) return false;
    this.unbreakableCd = GameConfig.voidTree.unbreakable.cooldown;
    return true;
  }

  private die(): void {
    this.state = "dead";
    this.velocity.set(0, 0, 0);
    this.knockback.set(0, 0, 0);
    this.attackPhase = "none";
    this.sprintTimer = 0;
    this.group.visible = true;
    this.bodyMat.emissiveIntensity = 0.1;
    this.rigged?.anim.play("Death", { loop: false });
    events.emit("player:died", undefined);
  }

  /** Respawn genérico (origem, HP cheio). Mantido por compatibilidade. */
  respawn(): void {
    this.respawnAt(0, 0, 1);
  }

  /** Reaparece em (x,z) com uma fração do MaxHP e proteção pós-respawn. */
  respawnAt(x: number, z: number, hpFraction: number): void {
    this.group.position.set(x, 0, z);
    this.velocity.set(0, 0, 0);
    this.knockback.set(0, 0, 0);
    this.facingAngle = 0;
    this.state = "idle";
    this.attackPhase = "none";
    this.sprintTimer = 0;
    this.sprintCooldown = 0;
    this.currentTarget = null;
    this.sinceHit = 999;
    this.bodyMat.emissiveIntensity = 0.6;
    this.health.reset();
    this.health.current = Math.max(1, Math.round(this.health.max * clamp(hpFraction, 0, 1)));
    this.spawnProtect = GameConfig.respawn.invuln;
    this.rigged?.anim.play("Idle", { crossfade: 0.1 });
    events.emit("player:respawn", undefined);
  }

  /** Reposiciona (troca de região) preservando HP/arma; breve proteção pós-chegada. */
  teleportTo(x: number, z: number): void {
    this.group.position.set(x, 0, z);
    this.velocity.set(0, 0, 0);
    this.knockback.set(0, 0, 0);
    this.spawnProtect = GameConfig.respawn.invuln;
    this.sinceHit = 999;
    this._combatEnabled = false;
    this._inSanctuary = true;
    this.currentTarget = null;
  }

  /** Regeneração da Void Sanctuary: cura % do MaxHP/s se passou o delay sem tomar dano. */
  regenInSanctuary(dt: number): void {
    if (this.isDead) return;
    if (this.sinceHit < GameConfig.sanctuary.playerRegenDelay) return;
    if (this.health.current >= this.health.max) return;
    this.health.heal(GameConfig.sanctuary.playerRegenRate * this.health.max * dt);
  }

  /** Recuperação percentual FORA da Sanctuary (após ficar sem dano). true se curou (§ codex). */
  regenInField(dt: number): boolean {
    if (this.isDead) return false;
    const cfg = GameConfig.fieldRegen;
    if (this.sinceHit < cfg.delay) return false;
    if (this.health.current >= this.health.max) return false;
    this.health.heal(cfg.rate * this.health.max * dt);
    return true;
  }

  /** Fração de recarga do sprint (0 = pronto, 1 = acabou de usar). */
  sprintCooldownFraction(): number {
    return clamp(this.sprintCooldown / Math.max(0.001, this.effSprintCooldown), 0, 1);
  }
}
